import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import {
  makeStyles,
  Tab,
  Tabs,
  Box,
  Typography,
  CardHeader,
  Card,
  CardContent,
  IconButton,
} from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";

import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import { format } from "date-fns";
import GeneralContent from "./generalContent";

import CloseIcon from "@material-ui/icons/Close";
import AddCircleIcon from "@material-ui/icons/AddCircle";

import editorConfiguration from "../../../../util/EditorConfiguration";
import { toast } from "react-toastify";

const frontendUrl = process.env.REACT_APP_CINEFAN_WEB_HOSTNAME;

const useStyles = makeStyles((theme) => ({
  body: {
    minWidth: "calc(100vw - 240px)",
    minHeight: "calc(100vh - 64px)",
    margin: "-32px -48px",
    backgroundColor: "#E5E5E5",
  },
  content: {
    width: "100%",
    padding: "37px 50px",
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  h4: {
    marginBottom: "40px",
    color: "#333333",
  },
  h6: {
    marginBottom: "13px",
    color: "#333333",
  },
  languageTabs: {
    display: "flex",
    "& div": {
      padding: "15px 20px",
      color: "#333333",
      fontSize: "14px",
      cursor: "pointer",
      "&.active": {
        color: "#306F9D",
        backgroundColor: "white",
        borderTop: "2px solid #306F9D",
      },
    },
  },
  details: {
    backgroundColor: "white",
    padding: "35px 20px",
  },
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  permalink: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    marginBottom: "40px",
    color: "#333333",
    "& .link": {
      color: "#306F9D",
      margin: "0 10px",
      display: "flex",
      alignItems: "center",
      "& input": {
        padding: "7px 14px",
        fontSize: "13px",
      },
    },
    "& button": {
      padding: "2px 20px",
      textTransform: "inherit",
      color: "#727272",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  card: {
    borderRadius: "0",
    marginBottom: "20px",
  },
  cardHeader: {
    color: "#306F9D",
    backgroundColor: "#EDEEF0",
    borderLeft: "3px solid #306F9D",
    padding: "12px 15px",
    "& span": {
      fontSize: "14px",
      fontWeight: "bold",
    },
  },
}));

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

const PagesDetailsPage = ({ disabled, ...props }) => {
  const classes = useStyles();
  const id = props.match.params.pageId;

  const [activeTab, setActiveTab] = useState(0);
  const [isSlugInvalid, setIsSlugInvalid] = React.useState(false);
  const [isEditPermalink, setEditPermalink] = React.useState(false);

  const [details, setDetails] = useState(null);
  const [updatedTabs, setUpdatedTabs] = useState(null);
  const { setLoading } = useLoading();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    console.log(data);
    const params = data;
    params.category = "general";
    params.id = Number(id);
    params.publishDate = format(new Date(), "yyyy-MM-dd kk:mm:ss");
    params.status = "PUBLISHED";

    if (!params.localizations.tc) {
      params.localizations.tc = details.localizations.tc;
    }

    if (!details.tab) {
      params.tab = false;
    } else {
      params.tab = true;
      console.log(params);
      params.tabs.forEach((tabDetails, idx) => {
        console.log(tabDetails);
        if (!tabDetails.localizations.tc)
          tabDetails.localizations.tc = updatedTabs[idx].localizations.tc;
      });
    }

    console.log(params);
    if (!params.localizations.en.slug || !params.localizations.tc.slug) {
      toast.error("Slug required.");
      setLoading(false);
      return;
    }
    try {
      const result = await apiHelper.cinefan.updatePageById(id, params);
      console.log(result.data);
      toast.dark("Your changes is saved.");
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getDetail();
  }, []);

  useEffect(() => {
    if (details) {
      setUpdatedTabs(details.tabs);
    }
  }, [details]);

  const getDetail = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.cinefan.getPageById(id);
      setDetails(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id]);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const removeTab = (id) => {
    const newarray = updatedTabs.filter(function (element, index) {
      return index !== id;
    });
    setUpdatedTabs(newarray);

    // update form values
    const formTabValues = getValues("tabs");
    const newFormTabsValue = formTabValues.filter(function (element, index) {
      return index !== id;
    });
    console.log(newFormTabsValue);
    reset({ ...getValues(), tabs: newFormTabsValue });
  };

  const addTab = () => {
    const newValue = {
      localizations: {
        en: {
          name: "",
          content: "",
        },
        tc: {
          name: "",
          content: "",
        },
      },
    };
    setUpdatedTabs(updatedTabs.concat(newValue));
  };

  React.useEffect(() => {
    if (details) reset(details);
  }, [details, reset])

  return (
    details && (
      <div className={classes.body}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={disabled} className="fieldset">
            <div className={classes.content}>
              <Typography variant="h4" className={classes.h4}>
                Pages > Edit Page
              </Typography>
              <Tabs
                value={activeTab}
                onChange={handleChange}
                aria-label="nav tabs example"
                TabIndicatorProps={{
                  style: { top: "0", background: "#306F9D" },
                }}
              >
                <Tab
                  label="English"
                  {...a11yProps(0)}
                />
                <Tab
                  label="繁體中文"
                  {...a11yProps(1)}
                />
              </Tabs>
              <div className={classes.details}>
                <TabPanel value={activeTab} index={0}>
                  {!details.tab && (
                    <GeneralContent
                      details={details}
                      activeLang={"en"}
                      control={control}
                      register={register}
                      setValue={setValue}
                      getValues={getValues}
                      disabled={disabled}
                      isEditPermalink={isEditPermalink}
                      setEditPermalink={setEditPermalink}
                      isSlugInvalid={isSlugInvalid}
                      setIsSlugInvalid={setIsSlugInvalid}
                    />
                  )}
                  {details.tab && (
                    <>
                      <Controller
                        name={`localizations.en.title`}
                        control={control}
                        defaultValue={details.localizations.en.title}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Page Title"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                      <div className={classes.permalink}>
                        Permalink:
                        <div className="link">
                          {frontendUrl || "https://cinefan.com.hk"}/
                          {details.localizations.en.slug}
                          <input
                            value={details.localizations.en.slug}
                            type="hidden"
                            {...register(`localizations.en.slug`)}
                          />
                        </div>
                      </div>
                      <Typography variant="h6" className={classes.h6}>
                        Tabs
                      </Typography>

                      {updatedTabs &&
                        updatedTabs.map((tabDeatils, idx) => {
                          return (
                            <Card variant="outlined" className={classes.card}>
                              <CardHeader
                                title={`Tab ${idx + 1}`}
                                className={classes.cardHeader}
                                action={
                                  disabled || (
                                    <IconButton
                                      aria-label="delete"
                                      size="small"
                                      onClick={() => {
                                        removeTab(idx);
                                      }}
                                    >
                                      <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                  )
                                }
                              />
                              <CardContent>
                                <Controller
                                  name={`tabs.${idx}.localizations.en.name`}
                                  control={control}
                                  defaultValue={
                                    tabDeatils.localizations.en.name
                                  }
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      key={tabDeatils.localizations.en.name}
                                      label="Tab Name"
                                      variant="outlined"
                                      margin="dense"
                                      className={classes.textInput}
                                      {...field}
                                      error={!!error}
                                      helperText={error ? error.message : ""}
                                    />
                                  )}
                                />
                                <Typography variant="h6" className={classes.h6}>
                                  Tab content
                                </Typography>
                                <CKEditor
                                  editor={Editor}
                                  config={editorConfiguration}
                                  data={
                                    tabDeatils.localizations.en.content || ""
                                  }
                                  disabled={disabled}
                                  onReady={(editor) => {
                                    const data = editor.getData();
                                    setValue(
                                      `tabs.${idx}.localizations.en.content`,
                                      data
                                    );
                                  }}
                                  onChange={(event, editor) => {
                                    const data = editor.getData();
                                    setValue(
                                      `tabs.${idx}.localizations.en.content`,
                                      data
                                    );
                                  }}
                                />
                              </CardContent>
                            </Card>
                          );
                        })}
                      {disabled || (
                        <Button
                          style={{ color: "#306F9D", fontSize: "14px" }}
                          onClick={() => {
                            addTab();
                          }}
                        >
                          <AddCircleIcon size="small" /> Add New Tab
                        </Button>
                      )}
                    </>
                  )}
                </TabPanel>
                {/*  start of chinese tab */}

                <TabPanel value={activeTab} index={1}>
                  {!details.tab && (
                    <GeneralContent
                      details={details}
                      activeLang={"tc"}
                      control={control}
                      register={register}
                      setValue={setValue}
                      getValues={getValues}
                      disabled={disabled}
                      isEditPermalink={isEditPermalink}
                      setEditPermalink={setEditPermalink}
                      isSlugInvalid={isSlugInvalid}
                      setIsSlugInvalid={setIsSlugInvalid}
                    />
                  )}
                  {details.tab && (
                    <>
                      <Controller
                        name={`localizations.tc.title`}
                        control={control}
                        defaultValue={details.localizations.tc.title}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            label="Page Title"
                            variant="outlined"
                            margin="dense"
                            className={classes.textInput}
                            {...field}
                            error={!!error}
                            helperText={error ? error.message : ""}
                          />
                        )}
                      />
                      <div className={classes.permalink}>
                        Permalink:
                        <div className="link">
                          {frontendUrl || "https://cinefan.com.hk"}/zh/
                          {details.localizations.tc.slug}
                          <input
                            value={details.localizations.tc.slug}
                            type="hidden"
                            {...register(`localizations.tc.slug`)}
                          />
                        </div>
                      </div>
                      <Typography variant="h6" className={classes.h6}>
                        Tabs
                      </Typography>

                      {updatedTabs &&
                        updatedTabs.map((tabDeatils, idx) => {
                          return (
                            <Card variant="outlined" className={classes.card}>
                              <CardHeader
                                title={`Tab ${idx + 1}`}
                                className={classes.cardHeader}
                                action={
                                  disabled || (
                                    <IconButton
                                      aria-label="delete"
                                      size="small"
                                      onClick={() => {
                                        removeTab(idx);
                                      }}
                                    >
                                      <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                  )
                                }
                              />
                              <CardContent>
                                <Controller
                                  name={`tabs.${idx}.localizations.tc.name`}
                                  control={control}
                                  defaultValue={
                                    tabDeatils.localizations.tc.name
                                  }
                                  render={({
                                    field,
                                    fieldState: { error },
                                  }) => (
                                    <TextField
                                      key={tabDeatils.localizations.tc.name}
                                      label="Tab Name"
                                      variant="outlined"
                                      margin="dense"
                                      className={classes.textInput}
                                      {...field}
                                      error={!!error}
                                      helperText={error ? error.message : ""}
                                    />
                                  )}
                                />
                                <Typography variant="h6" className={classes.h6}>
                                  Tab content
                                </Typography>
                                <CKEditor
                                  editor={Editor}
                                  config={editorConfiguration}
                                  data={
                                    tabDeatils.localizations.tc.content || ""
                                  }
                                  disabled={disabled}
                                  onReady={(editor) => {
                                    const data = editor.getData();
                                    setValue(
                                      `tabs.${idx}.localizations.tc.content`,
                                      data
                                    );
                                  }}
                                  onChange={(event, editor) => {
                                    const data = editor.getData();
                                    setValue(
                                      `tabs.${idx}.localizations.tc.content`,
                                      data
                                    );
                                  }}
                                />
                              </CardContent>
                            </Card>
                          );
                        })}
                      {disabled || (
                        <Button
                          style={{ color: "#306F9D", fontSize: "14px" }}
                          onClick={() => {
                            addTab();
                          }}
                        >
                          <AddCircleIcon size="small" /> Add New Tab
                        </Button>
                      )}
                    </>
                  )}
                </TabPanel>
              </div>
              {disabled || (
                <div className={classes.buttonGroup}>
                  <Button style={{ marginRight: "20px" }}>Preview</Button>
                  <Button
                    variant="contained"
                    disableElevation
                    style={{
                      color: "white",
                      background: "#306F9D",
                      marginRight: "5px",
                      padding: "9px 16px",
                    }}
                    type="submit"
                  >
                    Publish
                  </Button>
                </div>
              )}
            </div>
          </fieldset>
        </form>
      </div>
    )
  );
};

export default PagesDetailsPage;
